import { QuestionForm } from "components/EmbeddedForm/QuestionForm";
import { ReactComponent as SuccessIcon } from "images/success.svg";
import { formModes } from "EmbeddedForm.hook";
import { toast } from "react-toastify";
import { toastifyConfig } from "../ReferralForm/ReferralForm.common";
import { useForm } from "react-hook-form";
import { Stack } from "@mui/material";
import { useLocationsAndProvidersOptions } from "hooks/useLocationsAndProvidersOptions";
import { useEffect } from "react";
import { defaultPractice, defaultSender } from "EmbeddedForm";
import {
  mapNewPracticeWithNewSenderQuestionPayload,
  replaceNullableValuesWithNull,
} from "utils";

/** @type {import("types").NewQuestionEmbeddedFormData} */
const defaultValues = {
  dashboardAccessToken: "",
  providerId: "",
  externalForm: "",
  question: "",
  locationId: "",
};

export const CreateQuestion = ({
  formCode,
  originator,
  providers,
  recipient,
  locations,
  setMode,
  setLoading,
  setError,
  loading,
  reloadAuthData,
  handleLogout,
  loginState,
  setLoginState,
  setProviders,
}) => {
  const {
    control,
    setValue,
    reset,
    watch,
    handleSubmit,
    setError: setFormError,
    clearErrors,
  } = useForm({
    defaultValues: {
      ...defaultPractice,
      ...defaultSender,
      ...defaultValues,
      dashboardAccessToken: originator?.dashboardAccessToken,
      externalForm: formCode,
    },
  });

  const handleResetAndBack = () => {
    reset();
    handleClose();
  };

  const handleClose = () => setMode(formModes.START);

  const showSuccessNotification = () =>
    toast(
      () => (
        <Stack direction={"row"} gap={1}>
          <SuccessIcon />
          <Stack>
            Question sent.
            <div className="text-sm">
              Thank you for reaching out to {recipient?.name}. They will contact
              you shortly.
            </div>
          </Stack>
        </Stack>
      ),
      toastifyConfig
    );

  const onSubmit = async (values) => {
    const isNewSender = !values?.dashboardAccessToken;

    const preparedValues = isNewSender
      ? mapNewPracticeWithNewSenderQuestionPayload(values)
      : replaceNullableValuesWithNull(values);

    try {
      setLoading(true);
      const response = await fetch(
        isNewSender
          ? `${process.env.REACT_APP_API_V2_URL}/referral/question/external/new-sender`
          : `${process.env.REACT_APP_API_V2_URL}/referral/question/external`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(preparedValues),
        }
      );
      const result = await response.json();

      if (result?.error || result?.message) {
        setError(result?.message || result?.error || "Something went wrong");
        return;
      }

      showSuccessNotification();
      handleClose();
    } catch (err) {
      setError(err?.message ?? "Something went wrong. Call support");
    } finally {
      setLoading(false);
    }
  };

  const {
    isLocationsPresent,
    isMoreThanOneProvider,
    locationOptions,
    providerOptions,
  } = useLocationsAndProvidersOptions({
    locations,
    providers,
    setValue,
  });

  useEffect(() => {
    if (originator) {
      setValue("dashboardAccessToken", originator.dashboardAccessToken, {
        shouldDirty: false,
      });
      setValue("externalForm", formCode, {
        shouldDirty: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originator]);

  return (
    <QuestionForm
      control={control}
      handleResetAndBack={handleResetAndBack}
      recipient={recipient}
      loading={loading}
      handleClose={handleClose}
      onQuestionSubmit={handleSubmit(onSubmit)}
      isButtonEnabled={!loading}
      locationOptions={locationOptions}
      providerOptions={providerOptions}
      isLocationsPresent={isLocationsPresent}
      isMoreThanOneProvider={isMoreThanOneProvider}
      originator={originator}
      watch={watch}
      setError={setError}
      setFormError={setFormError}
      clearErrors={clearErrors}
      reloadAuthData={reloadAuthData}
      handleLogout={handleLogout}
      loginState={loginState}
      setLoginState={setLoginState}
      setLoading={setLoading}
      setValue={setValue}
      setProviders={setProviders}
      reset={reset}
    />
  );
};
